import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { AnimatedImageApi } from "../api/animated-image-api";
import { queryClient } from "./query-client";
import Toast from "@/lib/components/Toast";

export function useQuickAnimationCreditsQuery({
  teamExternalId,
  sourceImageExternalIds,
}: {
  teamExternalId: string;
  sourceImageExternalIds: string[];
}) {
  return useQuery({
    queryKey: animatedImageQueryKeys.getQuickAnimationCredits(
      teamExternalId,
      sourceImageExternalIds,
    ),
    queryFn: () =>
      AnimatedImageApi.getQuickAnimationCredits(
        teamExternalId!,
        sourceImageExternalIds,
      ),
    staleTime: 0,
  });
}

export function useAnimatedImagesByOriginVideoIdQuery(
  teamExternalId: string,
  videoExternalId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: animatedImageQueryKeys.getByOriginVideoExternalId(
      teamExternalId,
      videoExternalId,
    ),
    queryFn: () =>
      AnimatedImageApi.getAnimatedImagesByOriginVideoExternalId(
        teamExternalId,
        videoExternalId,
      ),
    enabled: (options?.enabled ?? true) && !!videoExternalId,
    staleTime: 0,
    meta: {
      errorMessage: "Failed to fetch animated images",
    },
  });
}

type AnimatedImageCursor =
  | { createdAt: string; externalId: string }
  | undefined;

export function usePagedAnimatedImagesQuery(teamExternalId: string) {
  return useInfiniteQuery({
    queryKey: animatedImageQueryKeys.paged(teamExternalId),
    queryFn: ({ pageParam }) => {
      return AnimatedImageApi.getPagedAnimatedImages(teamExternalId, {
        pageSize: 50,
        cursorCreatedAt: pageParam?.createdAt,
        cursorExternalId: pageParam?.externalId,
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as AnimatedImageCursor,
    staleTime: 0,
  });
}

export function useDeleteAnimatedImagesMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationKey: animatedImageQueryKeys.delete(teamExternalId),
    mutationFn: (externalIds: string[]) =>
      AnimatedImageApi.deleteAnimatedImages(teamExternalId, externalIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: animatedImageQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to delete animations");
    },
  });
}

export const animatedImageQueryKeys = {
  all: () => ["animatedImage"],
  paged: (teamExternalId: string) => [
    "animatedImage",
    "paged",
    { teamExternalId },
  ],
  getByOriginVideoExternalId: (
    teamExternalId: string,
    videoExternalId: string,
  ) => ["animatedImage", "list", { teamExternalId, videoExternalId }],
  delete: (teamExternalId: string) => [
    "animatedImage",
    "delete",
    { teamExternalId },
  ],
  quickAnimationCredits: () =>
    ["animatedImage", "quick-animation-credits"] as const,
  getQuickAnimationCredits: (
    teamExternalId: string | undefined,
    sourceImageExternalIds: string[],
  ) =>
    [
      "animatedImage",
      "quick-animation-credits",
      { teamExternalId, sourceImageExternalIds },
    ] as const,
};
